import { useCallback, useEffect, useRef, useState } from 'react';
import {
  startStream as mjpegStartStream,
  stopStream as mjpegStopStream,
  getStreamStatus as mjpegGetStreamStatus,
  getSnapshot as mjpegGetSnapshot,
  isLikelyRealMjpegSnapshot,
  MJPEG_LIVE_READY_SNAPSHOT_STREAK,
  PAGE_VIEWER_HEARTBEAT_MS,
  heartbeatViewer,
  releaseViewer,
} from '../services/mjpegApi';
import { preferredMjpegModeForCamera } from '../services/mjpegMode';

/** Spatial Operations Camera uses the existing raw Live View transport only. */
export const SPATIAL_OPERATIONS_MJPEG_VIEW_KIND = 'live' as const;

/**
 * Live Booth may request the existing annotated MJPEG relay.
 * Raw stalls stay bounded. Annotated stays selected and keeps retrying so a
 * dead relay cannot freeze the last JPEG with no worker and no further start.
 */
export const LIVE_BOOTH_ANNOTATED_RECONNECT_BUDGET = 3;
export const LIVE_BOOTH_MULTIPART_RECONNECT_MAX_ATTEMPTS = 8;
export const LIVE_BOOTH_MULTIPART_RECONNECT_BASE_DELAY_MS = 500;
export const LIVE_BOOTH_MULTIPART_RECONNECT_MAX_DELAY_MS = 8_000;
/** While the same view stays selected, retries continue at this cadence. */
export const SELECTED_VIEW_MJPEG_RETRY_DELAYS_MS = [1_000, 2_000, 3_000, 5_000] as const;

export function selectedViewMjpegRetryDelayMs(attempt: number): number {
  const index = Math.max(0, Math.floor(attempt) - 1);
  return SELECTED_VIEW_MJPEG_RETRY_DELAYS_MS[
    Math.min(index, SELECTED_VIEW_MJPEG_RETRY_DELAYS_MS.length - 1)
  ];
}

export function selectedMjpegPath(
  cameraId: string,
  edgeDeviceId?: string | null,
  viewKind: SpatialOperationsMjpegViewKind = 'live',
): string {
  const params = new URLSearchParams();
  const deviceId = String(edgeDeviceId || '').trim();
  if (deviceId) params.set('edge_device_id', deviceId);
  params.set('view_kind', viewKind);
  return `/api/v1/mjpeg/streams/${encodeURIComponent(cameraId)}/mjpeg?${params.toString()}`;
}
export const LIVE_BOOTH_ANNOTATED_MAX_FRAME_AGE_MS = 12_000;
export const MIS_STICKY_ANNOTATED_DEVICE_ID = 'demo5';
export const MIS_STICKY_ANNOTATED_CAMERA_ID = 'cam_a1bc71b70d77';
/** How often a raw fallback rechecks for a real annotated frame. */
export const MIS_STICKY_ANNOTATED_POLL_MS = 1_000;

export type SpatialOperationsMjpegViewKind = 'live' | 'annotated';

export type SpatialOperationsLiveCameraState =
  | 'idle'
  | 'connecting'
  | 'live'
  | 'fallback_snapshot'
  | 'offline'
  | 'error';

/** `raw_fallback` is not annotated uptime. Monitors must record it separately. */
export type SpatialOperationsVideoSource = 'live' | 'annotated' | 'raw_fallback';

export type SpatialOperationsLiveCameraView = {
  status: SpatialOperationsLiveCameraState;
  mjpegUrl: string | null;
  reconnect?: () => void;
  /** Explicit operator/session cleanup; view changes only detach the browser. */
  stop?: () => Promise<void>;
  warmAnnotatedUrl?: string | null;
  videoSource?: SpatialOperationsVideoSource;
  reconnectAttempts?: number;
  fallbackReason?: string | null;
  /** A distinct multipart JPEG was painted. Resets the current outage budget. */
  noteFreshFrame?: () => void;
};

export function assembleBrowserMjpegUrl(mjpegPath: string, nowMs = Date.now()): string {
  const next = String(mjpegPath || '');
  if (!next) return next;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const absolute = next.startsWith('http://') || next.startsWith('https://')
    ? next
    : `${origin}${next}`;
  const stamp = `_=${nowMs}`;
  if (/[?&]_=\d+/.test(absolute)) {
    return absolute.replace(/([?&])_=\d+/, `$1${stamp}`);
  }
  return `${absolute}${absolute.includes('?') ? '&' : '?'}${stamp}`;
}

export function liveMjpegReadyDeadlineMs(cameraId: string): number {
  return cameraId.startsWith('cam_') ? 30_000 : 60_000;
}

export function parseBackendUtcTimestampMs(value: unknown): number {
  const text = String(value || '').trim();
  if (!text) return Number.NaN;
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(text);
  return Date.parse(hasTimezone ? text : `${text}Z`);
}

function isFreshAnnotatedStatus(
  status: Awaited<ReturnType<typeof mjpegGetStreamStatus>>,
  nowMs = Date.now(),
): boolean {
  const lastFrameMs = parseBackendUtcTimestampMs(status.last_frame_at);
  return status.ok
    && status.running === true
    && status.state === 'RUNNING'
    && status.view_kind === 'annotated'
    && Number.isFinite(lastFrameMs)
    && nowMs - lastFrameMs >= 0
    && nowMs - lastFrameMs <= LIVE_BOOTH_ANNOTATED_MAX_FRAME_AGE_MS;
}

function freshAnnotatedStatusUrl(
  status: Awaited<ReturnType<typeof mjpegGetStreamStatus>>,
  cameraId: string,
  edgeDeviceId: string | undefined,
): string | null {
  if (
    !isFreshAnnotatedStatus(status)
    || status.camera_id !== cameraId
    || (edgeDeviceId && status.device_id !== edgeDeviceId)
    || !status.mjpeg_url
  ) return null;
  const parsed = new URL(status.mjpeg_url, window.location.origin);
  if (
    parsed.pathname !== `/api/v1/mjpeg/streams/${cameraId}/mjpeg`
    || parsed.searchParams.get('view_kind') !== 'annotated'
    || (edgeDeviceId && parsed.searchParams.get('edge_device_id') !== edgeDeviceId)
  ) return null;
  return assembleBrowserMjpegUrl(status.mjpeg_url);
}

async function releaseMjpegKind(
  cameraId: string,
  edgeDeviceId: string | undefined,
  kind: SpatialOperationsMjpegViewKind,
): Promise<void> {
  try {
    await mjpegStopStream(cameraId, edgeDeviceId, kind);
  } catch {
    // Best-effort. The browser attaches only the selected URL.
  }
}

async function waitForRealLiveFrame(args: {
  cameraId: string;
  edgeDeviceId?: string;
  viewKind: SpatialOperationsMjpegViewKind;
  isCurrent: () => boolean;
  deadlineMs: number;
}): Promise<boolean> {
  let realSnapshotStreak = 0;
  while (Date.now() < args.deadlineMs) {
    if (!args.isCurrent()) return false;
    try {
      const snapshot = await mjpegGetSnapshot(
        args.cameraId,
        args.edgeDeviceId,
        args.viewKind,
      );
      if (isLikelyRealMjpegSnapshot(snapshot)) {
        realSnapshotStreak += 1;
        if (realSnapshotStreak >= MJPEG_LIVE_READY_SNAPSHOT_STREAK) return true;
      } else {
        realSnapshotStreak = 0;
      }
    } catch {
      realSnapshotStreak = 0;
    }
    await new Promise((resolve) => setTimeout(resolve, 350));
  }
  return false;
}

export function useSpatialOperationsLiveCamera(args: {
  active: boolean;
  cameraId?: string | null;
  edgeDeviceId?: string | null;
  cameraOnline: boolean | null;
  cameraEnabled?: boolean | null;
  snapshotUrl?: string | null;
  readyDeadlineMs?: number;
  /** Default `live` keeps Spatial Operations and Room B on the raw relay. */
  viewKind?: SpatialOperationsMjpegViewKind;
  /** Demo5 showcase opt-in: preserve a proven annotated relay across view changes. */
  stickyAnnotated?: boolean;
}): SpatialOperationsLiveCameraView {
  const [status, setStatus] = useState<SpatialOperationsLiveCameraState>('idle');
  const [mjpegUrl, setMjpegUrl] = useState<string | null>(null);
  const [videoSource, setVideoSource] = useState<SpatialOperationsVideoSource>('live');
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [fallbackReason, setFallbackReason] = useState<string | null>(null);
  const [warmAnnotatedUrl, setWarmAnnotatedUrl] = useState<string | null>(null);
  const snapshotUrlRef = useRef(args.snapshotUrl);
  snapshotUrlRef.current = args.snapshotUrl;
  const requestedViewKind: SpatialOperationsMjpegViewKind = args.viewKind === 'annotated' ? 'annotated' : 'live';
  const liveArgsRef = useRef({
    cameraId: '',
    edgeDeviceId: undefined as string | undefined,
    active: false,
    offline: false,
    requestedViewKind: 'live' as SpatialOperationsMjpegViewKind,
    activeKind: 'live' as SpatialOperationsMjpegViewKind,
    reconnectAttempts: 0,
    readyDeadlineMs: undefined as number | undefined,
  });
  const reconnectInFlightRef = useRef(false);
  const hasAttachedStreamRef = useRef(false);
  const operationGenerationRef = useRef(0);
  const explicitlyStoppedRef = useRef(false);
  const runningKindRef = useRef<SpatialOperationsMjpegViewKind | null>(null);
  const stickyAnnotatedRef = useRef<{ key: string; url: string } | null>(null);
  const annotatedRetryTimerRef = useRef<number | null>(null);
  const pageViewerIdRef = useRef<{ key: string; id: string } | null>(null);

  const cameraId = String(args.cameraId || '').trim();
  const edgeDeviceId = String(args.edgeDeviceId || '').trim() || undefined;
  const enabled = args.cameraEnabled !== false;
  const offline = args.cameraOnline === false || !enabled;
  // Mutate in place. Replacing the object drops activeKind updates that land
  // after an await, and the next close would start another stream.
  liveArgsRef.current.cameraId = cameraId;
  liveArgsRef.current.edgeDeviceId = edgeDeviceId;
  liveArgsRef.current.active = args.active && !explicitlyStoppedRef.current;
  liveArgsRef.current.offline = offline;
  liveArgsRef.current.requestedViewKind = requestedViewKind;
  liveArgsRef.current.readyDeadlineMs = args.readyDeadlineMs;

  useEffect(() => {
    explicitlyStoppedRef.current = false;
    liveArgsRef.current.active = args.active;
    if (!args.active || !cameraId) {
      setMjpegUrl(null);
      setStatus(offline ? 'offline' : 'idle');
      return undefined;
    }
    if (offline) {
      setMjpegUrl(null);
      setStatus('offline');
      return undefined;
    }

    let cancelled = false;
    const operationGeneration = ++operationGenerationRef.current;
    const effectLiveArgs = liveArgsRef.current;
    const isCurrent = () => !cancelled && operationGenerationRef.current === operationGeneration;
    hasAttachedStreamRef.current = false;
    // Selected view is enough to open /mjpeg. A 404 reader must not block the GET.
    setMjpegUrl(assembleBrowserMjpegUrl(
      selectedMjpegPath(cameraId, edgeDeviceId, requestedViewKind),
    ));
    setStatus('connecting');
    setReconnectAttempts(0);
    setFallbackReason(null);
    liveArgsRef.current.reconnectAttempts = 0;
    liveArgsRef.current.activeKind = requestedViewKind;

    const fallback = (): SpatialOperationsLiveCameraState => (
      snapshotUrlRef.current ? 'fallback_snapshot' : 'error'
    );
    const deadlineMs = () => Date.now() + (args.readyDeadlineMs ?? liveMjpegReadyDeadlineMs(cameraId));

    const openKind = async (kind: SpatialOperationsMjpegViewKind): Promise<string | null> => {
      const sessionKey = `${edgeDeviceId || ''}:${cameraId}`;
      const sticky = kind === 'annotated' && args.stickyAnnotated
        ? stickyAnnotatedRef.current
        : null;
      if (sticky?.key === sessionKey) {
        try {
          const streamStatus = await mjpegGetStreamStatus(cameraId, edgeDeviceId, 'annotated');
          const refreshedUrl = freshAnnotatedStatusUrl(streamStatus, cameraId, edgeDeviceId);
          if (isCurrent() && refreshedUrl) {
            stickyAnnotatedRef.current = { key: sessionKey, url: refreshedUrl };
            setWarmAnnotatedUrl(refreshedUrl);
            return refreshedUrl;
          }
        } catch {
          // A stale session may be started again below; never claim it as healthy.
        }
      }
      const streamStatus = await mjpegStartStream(
        cameraId,
        undefined,
        edgeDeviceId,
        preferredMjpegModeForCamera(cameraId, kind),
        kind,
      );
      if (!isCurrent()) {
        if (!liveArgsRef.current.active || kind !== liveArgsRef.current.requestedViewKind) {
          void releaseMjpegKind(cameraId, edgeDeviceId, kind);
        }
        return null;
      }
      if (!streamStatus?.ok || !streamStatus.mjpeg_url) return null;
      const demandUrl = assembleBrowserMjpegUrl(streamStatus.mjpeg_url);
      // The multipart GET is what registers viewer demand and starts an idle reader.
      // Waiting for a healthy status first never issues that GET.
      setMjpegUrl(demandUrl);
      setStatus('connecting');
      const ready = await waitForRealLiveFrame({
        cameraId,
        edgeDeviceId,
        viewKind: kind,
        isCurrent,
        deadlineMs: deadlineMs(),
      });
      if (!isCurrent()) {
        if (!liveArgsRef.current.active || kind !== liveArgsRef.current.requestedViewKind) {
          void releaseMjpegKind(cameraId, edgeDeviceId, kind);
        }
        return null;
      }
      if (!ready) return null;
      if (kind === 'annotated' && args.stickyAnnotated) {
        const health = await mjpegGetStreamStatus(cameraId, edgeDeviceId, 'annotated');
        const healthUrl = freshAnnotatedStatusUrl(health, cameraId, edgeDeviceId);
        if (!isCurrent() || !healthUrl) return null;
        return healthUrl;
      }
      return assembleBrowserMjpegUrl(streamStatus.mjpeg_url);
    };

    void (async () => {
      try {
        const previousKind = runningKindRef.current;
        if (previousKind && previousKind !== requestedViewKind) {
          runningKindRef.current = null;
          const preserveAnnotated = args.stickyAnnotated && previousKind === 'annotated';
          if (!preserveAnnotated) {
            await releaseMjpegKind(cameraId, edgeDeviceId, previousKind);
          }
          if (!isCurrent()) return;
        }
        const primary = await openKind(requestedViewKind);
        if (!isCurrent()) return;
        if (primary) {
          if (args.stickyAnnotated && requestedViewKind === 'annotated') {
            stickyAnnotatedRef.current = {
              key: `${edgeDeviceId || ''}:${cameraId}`,
              url: primary,
            };
            setWarmAnnotatedUrl(primary);
          }
          runningKindRef.current = requestedViewKind;
          liveArgsRef.current.activeKind = requestedViewKind;
          setVideoSource(requestedViewKind === 'annotated' ? 'annotated' : 'live');
          setFallbackReason(null);
          setMjpegUrl(primary);
          setStatus('live');
          hasAttachedStreamRef.current = true;
          return;
        }
        if (requestedViewKind === 'annotated') {
          // A healthy sticky annotated relay stays up for a later switch back.
          // The browser still shows the original stream until annotated frames are real.
          if (!args.stickyAnnotated) {
            await releaseMjpegKind(cameraId, edgeDeviceId, 'annotated');
            if (!isCurrent()) return;
          }
          const raw = await openKind('live');
          if (!isCurrent()) return;
          if (raw) {
            runningKindRef.current = 'live';
            liveArgsRef.current.activeKind = 'live';
            setVideoSource('raw_fallback');
            setFallbackReason('annotated_unready');
            setMjpegUrl(raw);
            setStatus('live');
            hasAttachedStreamRef.current = true;
            return;
          }
        }
        setMjpegUrl(assembleBrowserMjpegUrl(
          selectedMjpegPath(cameraId, edgeDeviceId, requestedViewKind),
        ));
        setStatus(fallback());
      } catch {
        if (!isCurrent()) return;
        setMjpegUrl(assembleBrowserMjpegUrl(
          selectedMjpegPath(cameraId, edgeDeviceId, requestedViewKind),
        ));
        setStatus(fallback());
      }
    })();

    return () => {
      cancelled = true;
      if (operationGenerationRef.current === operationGeneration) {
        operationGenerationRef.current += 1;
        effectLiveArgs.active = false;
      }
      if (annotatedRetryTimerRef.current != null) {
        window.clearTimeout(annotatedRetryTimerRef.current);
        annotatedRetryTimerRef.current = null;
      }
      // Detach only. The MJPEG worker is shared per (device, camera, view_kind).
      // stopStream() would terminate Live View / other tabs on the same session.
    };
  }, [args.active, args.stickyAnnotated, cameraId, edgeDeviceId, offline, args.readyDeadlineMs, requestedViewKind]);

  useEffect(() => {
    if (!args.active || offline || !cameraId) return undefined;
    const ownership = `${cameraId}:${requestedViewKind}`;
    if (!pageViewerIdRef.current || pageViewerIdRef.current.key !== ownership) {
      pageViewerIdRef.current = {
        key: ownership,
        id: `page:${cameraId}:${requestedViewKind}`,
      };
    }
    const token = pageViewerIdRef.current.id;
    const beat = () => {
      try {
        const pending = heartbeatViewer(cameraId, token, edgeDeviceId, requestedViewKind);
        if (pending && typeof (pending as Promise<void>).catch === 'function') {
          void (pending as Promise<void>).catch(() => {});
        }
      } catch {
        // A failed heartbeat must not tear down the picture. The lease expires.
      }
    };
    beat();
    const timer = window.setInterval(beat, PAGE_VIEWER_HEARTBEAT_MS);
    return () => {
      window.clearInterval(timer);
      try {
        const pending = releaseViewer(cameraId, token, edgeDeviceId, requestedViewKind);
        if (pending && typeof (pending as Promise<void>).catch === 'function') {
          void (pending as Promise<void>).catch(() => {});
        }
      } catch {
        // Release is best-effort. The lease still expires.
      }
    };
  }, [args.active, cameraId, edgeDeviceId, offline, requestedViewKind]);

  const adoptAnnotatedInFlightRef = useRef(false);
  useEffect(() => {
    if (!args.active || offline || !cameraId || requestedViewKind !== 'annotated' || videoSource !== 'raw_fallback') {
      return undefined;
    }
    let cancelled = false;
    const stillAnnotatedRequest = () => (
      !cancelled
      && liveArgsRef.current.active
      && liveArgsRef.current.requestedViewKind === 'annotated'
    );
    const timer = window.setInterval(() => {
      if (adoptAnnotatedInFlightRef.current) return;
      adoptAnnotatedInFlightRef.current = true;
      void (async () => {
        try {
          if (!stillAnnotatedRequest()) return;
          const confirmFrames = async (): Promise<boolean> => waitForRealLiveFrame({
            cameraId,
            edgeDeviceId,
            viewKind: 'annotated',
            isCurrent: stillAnnotatedRequest,
            deadlineMs: Date.now() + Math.max(1_500, args.readyDeadlineMs ?? 1_500),
          });
          let status = await mjpegGetStreamStatus(cameraId, edgeDeviceId, 'annotated');
          if (!stillAnnotatedRequest()) return;
          let url = freshAnnotatedStatusUrl(status, cameraId, edgeDeviceId);
          if (!url) {
            const started = await mjpegStartStream(
              cameraId,
              undefined,
              edgeDeviceId,
              preferredMjpegModeForCamera(cameraId, 'annotated'),
              'annotated',
            );
            if (!stillAnnotatedRequest() || !started?.ok || !started.mjpeg_url) return;
            const ready = await confirmFrames();
            if (!ready || !stillAnnotatedRequest()) return;
            status = await mjpegGetStreamStatus(cameraId, edgeDeviceId, 'annotated');
            url = freshAnnotatedStatusUrl(status, cameraId, edgeDeviceId);
          } else {
            const ready = await confirmFrames();
            if (!ready || !stillAnnotatedRequest()) return;
          }
          if (!url || !stillAnnotatedRequest()) return;
          if (args.stickyAnnotated) {
            stickyAnnotatedRef.current = { key: `${edgeDeviceId || ''}:${cameraId}`, url };
            setWarmAnnotatedUrl(url);
          }
          runningKindRef.current = 'annotated';
          liveArgsRef.current.activeKind = 'annotated';
          setFallbackReason(null);
          setMjpegUrl(url);
          setVideoSource('annotated');
          setStatus('live');
          hasAttachedStreamRef.current = true;
        } catch {
          // Keep the original stream. The next poll can adopt annotated frames.
        } finally {
          adoptAnnotatedInFlightRef.current = false;
        }
      })();
    }, MIS_STICKY_ANNOTATED_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [args.active, args.readyDeadlineMs, args.stickyAnnotated, cameraId, edgeDeviceId, offline, requestedViewKind, videoSource]);

  const noteFreshFrame = useCallback(() => {
    liveArgsRef.current.reconnectAttempts = 0;
    setReconnectAttempts(0);
    if (liveArgsRef.current.active && !liveArgsRef.current.offline) {
      setStatus('live');
    }
  }, []);

  const reconnect = useCallback(() => {
    const live = liveArgsRef.current;
    if (
      !live.active
      || !live.cameraId
      || live.offline
      || reconnectInFlightRef.current
      || annotatedRetryTimerRef.current != null
    ) return;
    const operationGeneration = operationGenerationRef.current;
    const isReconnectCurrent = () => (
      liveArgsRef.current.active && operationGenerationRef.current === operationGeneration
    );
    const annotatedSelected = live.requestedViewKind === 'annotated' && live.activeKind === 'annotated';
    liveArgsRef.current.reconnectAttempts += 1;
    const attempt = liveArgsRef.current.reconnectAttempts;
    setReconnectAttempts(attempt);
    setStatus('connecting');
    setFallbackReason(attempt > 0 ? 'stream_degraded' : null);
    const delayMs = selectedViewMjpegRetryDelayMs(attempt);
    annotatedRetryTimerRef.current = window.setTimeout(() => {
      annotatedRetryTimerRef.current = null;
      if (!isReconnectCurrent()) return;
      reconnectInFlightRef.current = true;
      void (async () => {
        let retryNeeded = false;
        try {
          const current = liveArgsRef.current;
          if (annotatedSelected) {
            // Drop the live claim before the next start so the last JPEG is not
            // presented as the current annotated picture while the relay is dead.
            setStatus('connecting');
            setVideoSource('annotated');
            setFallbackReason(null);
            const streamStatus = await mjpegStartStream(
              current.cameraId,
              undefined,
              current.edgeDeviceId,
              preferredMjpegModeForCamera(current.cameraId, 'annotated'),
              'annotated',
            );
            if (!isReconnectCurrent()) {
              if (explicitlyStoppedRef.current) {
                void releaseMjpegKind(current.cameraId, current.edgeDeviceId, 'annotated');
              }
              return;
            }
            if (liveArgsRef.current.requestedViewKind !== 'annotated') {
              return;
            }
            if (!streamStatus?.ok || !streamStatus.mjpeg_url) {
              setMjpegUrl(assembleBrowserMjpegUrl(selectedMjpegPath(
                current.cameraId,
                current.edgeDeviceId,
                'annotated',
              )));
              retryNeeded = true;
              return;
            }
            runningKindRef.current = 'annotated';
            liveArgsRef.current.activeKind = 'annotated';
            setVideoSource('annotated');
            setFallbackReason(null);
            setMjpegUrl(assembleBrowserMjpegUrl(streamStatus.mjpeg_url));
            setStatus('live');
            return;
          }
          const streamStatus = await mjpegStartStream(
            live.cameraId,
            undefined,
            live.edgeDeviceId,
            preferredMjpegModeForCamera(live.cameraId, 'live'),
            'live',
          );
          if (!isReconnectCurrent()) {
            if (explicitlyStoppedRef.current) {
              void releaseMjpegKind(live.cameraId, live.edgeDeviceId, 'live');
            }
            return;
          }
          if (!streamStatus?.ok || !streamStatus.mjpeg_url) {
            setMjpegUrl(assembleBrowserMjpegUrl(selectedMjpegPath(
              live.cameraId,
              live.edgeDeviceId,
              'live',
            )));
            retryNeeded = true;
            return;
          }
          setMjpegUrl(assembleBrowserMjpegUrl(streamStatus.mjpeg_url));
          setFallbackReason(null);
          setStatus('live');
        } catch {
          retryNeeded = true;
        } finally {
          reconnectInFlightRef.current = false;
          if (retryNeeded && isReconnectCurrent()) reconnect();
        }
      })();
    }, delayMs);
  }, []);

  useEffect(() => {
    let wasHidden = document.visibilityState === 'hidden';
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        wasHidden = true;
      } else if (wasHidden) {
        wasHidden = false;
        reconnect();
      }
    };
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) reconnect();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [reconnect]);

  const stop = useCallback(async () => {
    operationGenerationRef.current += 1;
    explicitlyStoppedRef.current = true;
    liveArgsRef.current.active = false;
    reconnectInFlightRef.current = false;
    if (annotatedRetryTimerRef.current != null) {
      window.clearTimeout(annotatedRetryTimerRef.current);
      annotatedRetryTimerRef.current = null;
    }
    const kinds = new Set<SpatialOperationsMjpegViewKind>();
    if (runningKindRef.current) kinds.add(runningKindRef.current);
    if (stickyAnnotatedRef.current) kinds.add('annotated');
    if (cameraId) kinds.add(liveArgsRef.current.requestedViewKind);
    runningKindRef.current = null;
    stickyAnnotatedRef.current = null;
    setWarmAnnotatedUrl(null);
    setMjpegUrl(null);
    setStatus('idle');
    await Promise.all(Array.from(kinds, (kind) => releaseMjpegKind(cameraId, edgeDeviceId, kind)));
  }, [cameraId, edgeDeviceId]);

  return {
    status,
    mjpegUrl,
    reconnect,
    stop,
    warmAnnotatedUrl,
    videoSource,
    reconnectAttempts,
    fallbackReason,
    noteFreshFrame,
  };
}
